import React from "react";
import { Link, useSearchParams } from "react-router-dom";
import SiteLayout from "@/components/SiteLayout";
import Icon from "@/components/Icon";
import { useReveal } from "@/hooks/useReveal";
import { ArrowRight, ChevronLeft } from "lucide-react";
import { EVARA, TEAM, VALUES, WHY_EVARA } from "@/data/evara";

const SECTION_TITLE = {
  who: "Who we are",
  mission: "Mission & values",
  why: "Why EVARA",
  leadership: "Leadership team",
};

function BackLink() {
  return (
    <div className="max-w-6xl mx-auto pt-8 px-6 md:px-10">
      <Link
        to="/about"
        className="inline-flex items-center gap-2 text-sm font-bold text-[var(--evara-primary)] hover:underline"
        data-testid="about-view-all-link"
      >
        <ChevronLeft size={16} /> View full About page
      </Link>
    </div>
  );
}

/* ---------------- Section components ---------------- */

function HeroSection({ compact = false }) {
  return (
    <section className="section-pad bg-sage-gradient" data-testid="about-hero">
      <div className="max-w-6xl mx-auto">
        <div className="eyebrow">About EVARA</div>
        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl mt-5 leading-[1.05]">
          Healthcare runs on people, process and evidence.<br />
          <em className="not-italic text-[var(--evara-primary)] font-medium">We work on all three.</em>
        </h1>
        {!compact && (
          <p className="mt-8 text-lg text-[var(--evara-text-2)] max-w-3xl leading-relaxed">
            {EVARA.about}
          </p>
        )}
      </div>
    </section>
  );
}

function WhoWeAreSection() {
  return (
    <section className="section-pad pt-16" data-testid="about-who">
      <div className="max-w-6xl mx-auto grid md:grid-cols-12 gap-10">
        <div className="md:col-span-5">
          <div className="eyebrow">Who we are</div>
          <h2 className="font-serif text-3xl md:text-5xl mt-4 leading-tight">
            A healthcare consultancy built{" "}
            <em className="not-italic text-[var(--evara-primary)] font-medium">
              inside operations, not around them.
            </em>
          </h2>
        </div>
        <div className="md:col-span-7">
          <p className="text-[var(--evara-text-2)] leading-relaxed text-base md:text-lg">
            {EVARA.about}
          </p>
          <p className="mt-5 text-[var(--evara-text-2)] leading-relaxed">
            We turn data, training and quality initiatives into measurable, sustainable improvement, partnering with healthcare leaders from baseline assessment through implementation and culture change.
          </p>
        </div>
      </div>
    </section>
  );
}

function MissionSection() {
  return (
    <>
      <section className="section-pad pt-16" data-testid="about-mission">
        <div className="max-w-6xl mx-auto grid md:grid-cols-12 gap-8">
          <div className="md:col-span-6 glass-card p-8 reveal">
            <div className="eyebrow">Mission</div>
            <p className="font-serif text-2xl md:text-3xl mt-4 leading-snug text-[var(--evara-text)]">
              {EVARA.mission}
            </p>
          </div>
          <div className="md:col-span-6 glass-card p-8 reveal">
            <div className="eyebrow">Vision</div>
            <p className="font-serif text-2xl md:text-3xl mt-4 leading-snug text-[var(--evara-text)]">
              {EVARA.vision}
            </p>
          </div>
        </div>
      </section>

      <section className="section-pad bg-soft-sage" data-testid="about-values">
        <div className="max-w-6xl mx-auto reveal">
          <div className="eyebrow">Our values</div>
          <h2 className="font-serif text-3xl md:text-5xl mt-4 leading-tight">
            Six principles that guide <em className="not-italic text-[var(--evara-primary)] font-medium">every engagement.</em>
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-12">
            {VALUES.map((v, i) => (
              <div key={v.title} className="card-evara p-6" data-testid={`about-value-${i}`}>
                <div className="font-serif text-xl text-[var(--evara-primary)]">{v.title}</div>
                <p className="mt-2 text-sm text-[var(--evara-text-2)] leading-relaxed">{v.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}

function WhyEvaraSection() {
  return (
    <section className="section-pad" data-testid="about-why">
      <div className="max-w-6xl mx-auto reveal">
        <div className="eyebrow">Why EVARA</div>
        <h2 className="font-serif text-3xl md:text-5xl mt-4 leading-tight">
          A consulting partner. <em className="not-italic text-[var(--evara-primary)] font-medium">not a vendor.</em>
        </h2>
        <p className="mt-6 text-[var(--evara-text-2)] max-w-3xl leading-relaxed">
          EVARA is not a generic management-consulting firm. We bring healthcare-specific expertise, analytical rigor and an implementation mindset to every engagement.
        </p>
        <div className="grid md:grid-cols-2 gap-5 mt-12">
          {WHY_EVARA.map((w, i) => (
            <div key={w.title} className="card-evara p-7 flex gap-5" data-testid={`about-why-${i}`}>
              <div className="icon-chip shrink-0">
                <Icon name={w.icon} size={20} />
              </div>
              <div>
                <h3 className="font-serif text-xl text-[var(--evara-primary)]">{w.title}</h3>
                <p className="text-sm mt-2 text-[var(--evara-text-2)] leading-relaxed">{w.body}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function LeadershipSection() {
  return (
    <section className="section-pad pt-16 bg-soft-sage" data-testid="about-team">
      <div className="max-w-6xl mx-auto reveal">
        <div className="eyebrow">Leadership</div>
        <h2 className="font-serif text-3xl md:text-5xl mt-4 leading-tight">
          Meet the team. <em className="not-italic text-[var(--evara-primary)] font-medium">senior on every engagement.</em>
        </h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5 mt-12">
          {TEAM.map((t, i) => (
            <div key={t.name} className="card-evara p-7 flex flex-col items-center text-center" data-testid={`about-team-card-${i}`}>
              {t.image ? (
                <img
                  src={t.image}
                  alt={t.name}
                  className="w-24 h-24 md:w-28 md:h-28 rounded-full object-cover border-2 border-[var(--evara-mint)]"
                  style={{ objectPosition: t.imagePosition || "center 20%" }}
                />
              ) : (
                <div
                  className="w-24 h-24 md:w-28 md:h-28 rounded-full border-2 border-[var(--evara-mint)] bg-[var(--evara-bg-mint)] flex items-center justify-center font-serif text-3xl text-[var(--evara-primary)]"
                  aria-label={t.name}
                >
                  {t.initials}
                </div>
              )}
              <div className="font-serif text-lg mt-5 text-[var(--evara-primary)]">{t.name}</div>
              <div className="text-[10px] uppercase tracking-[0.14em] text-[var(--evara-text-2)] mt-1.5">
                {t.role}
              </div>
              <p className="mt-4 text-sm text-[var(--evara-text-2)] leading-relaxed">{t.bio}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function CTASection() {
  return (
    <section className="section-pad pt-16" data-testid="about-cta">
      <div className="max-w-6xl mx-auto glass-card p-10 md:p-14 flex flex-col md:flex-row gap-8 items-center justify-between reveal">
        <div>
          <div className="eyebrow">Let&rsquo;s build something measurable</div>
          <h3 className="font-serif text-2xl md:text-4xl mt-3 leading-tight">
            30-minute discovery. <em className="not-italic text-[var(--evara-primary)] font-medium">Zero obligation.</em>
          </h3>
        </div>
        <Link to="/book" className="btn-primary" data-testid="about-book-btn">
          Schedule a Consultation <ArrowRight size={16} />
        </Link>
      </div>
    </section>
  );
}

/* ---------------- Page shell ---------------- */

export default function About() {
  useReveal();
  const [params] = useSearchParams();
  const section = params.get("section");
  const focused = section && SECTION_TITLE[section];

  if (focused) {
    return (
      <SiteLayout>
        <BackLink />
        {section === "who" && (
          <>
            <HeroSection compact />
            <WhoWeAreSection />
            <WhyEvaraSection />
            <CTASection />
          </>
        )}
        {section === "mission" && (
          <>
            <HeroSection compact />
            <MissionSection />
            <WhyEvaraSection />
            <CTASection />
          </>
        )}
        {section === "why" && (
          <>
            <HeroSection compact />
            <WhyEvaraSection />
            <CTASection />
          </>
        )}
        {section === "leadership" && (
          <>
            <HeroSection compact />
            <LeadershipSection />
            <CTASection />
          </>
        )}
      </SiteLayout>
    );
  }

  // Default: full page
  return (
    <SiteLayout>
      <HeroSection />
      <WhoWeAreSection />
      <MissionSection />
      <WhyEvaraSection />
      <LeadershipSection />
      <CTASection />
    </SiteLayout>
  );
}
